import {analytics} from './analytics';

/**
 * Recovery for the fatal Firestore JS SDK assertion
 * ("FIRESTORE (x.y.z) INTERNAL ASSERTION FAILED: Unexpected state (ID: ca9/b815)").
 *
 * When this assertion fires, the Firestore client's AsyncQueue is permanently
 * failed for the lifetime of the page: every subsequent read/write throws
 * `b815`. In practice this silently blanks any Firestore-backed view — most
 * painfully the post-session note screen, which reads the agent-written
 * transcript back from Firestore and otherwise just renders empty ("nothing
 * recorded / white screen"). The underlying bug is a watch-stream target race
 * (firebase-js-sdk #9267 / #9968), substantially fixed in firebase v12, but we
 * keep this guard for any residual occurrences.
 *
 * Strategy: detect the assertion globally, report it so we can measure the
 * residual rate, and recover a dead client by reloading the page ONCE — but
 * never while the user is mid-capture (a reload would interrupt the live
 * LiveKit scribe/interpreter session). On capture routes we only report; the
 * data is safe server-side and a manual refresh after the visit restores it.
 */

const ASSERTION_RE = /INTERNAL ASSERTION FAILED/i;
const ID_RE = /\(ID:\s*([0-9a-z]+)\)/i;

// Routes with a live LiveKit session in progress — reloading here would drop
// the recording, so we never auto-recover on these.
const CAPTURE_ROUTE_PREFIXES = [
  '/scribe',
  '/encounter',
  '/telephonic-encounter',
  '/group-encounter',
];

const RELOAD_GUARD_KEY = 'opalite_fs_recovery_reload_at';
const RELOAD_MIN_INTERVAL_MS = 60_000;

let installed = false;
let fatal = false;

/** True once the page has hit the unrecoverable Firestore assertion. */
export function isFirestoreFatal(): boolean {
  return fatal;
}

function onCaptureRoute(): boolean {
  const path = window.location.pathname || '';
  return CAPTURE_ROUTE_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`),
  );
}

function recentlyReloaded(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Number.isFinite(last) && Date.now() - last < RELOAD_MIN_INTERVAL_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — best-effort only.
  }
}

function handleAssertion(message: string): void {
  if (fatal) return; // The client only dies once per page load.
  fatal = true;

  const assertionId = (ID_RE.exec(message) || [])[1] || 'unknown';
  const onCapture = onCaptureRoute();
  const willReload = !onCapture && !recentlyReloaded();

  analytics.capture('firestore_internal_assertion', {
    assertion_id: assertionId,
    path: window.location.pathname,
    on_capture_route: onCapture,
    recovered_by_reload: willReload,
  });

  if (willReload) {
    markReloaded();
    // Defer so the telemetry event has a chance to flush before navigation.
    window.setTimeout(() => window.location.reload(), 400);
  }
}

function messageFrom(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.message}\n${value.stack || ''}`;
  try {
    return String((value as {message?: unknown}).message ?? value);
  } catch {
    return '';
  }
}

/**
 * Install global listeners that detect the fatal Firestore assertion and
 * recover. Safe to call once at app boot; no-ops in non-browser contexts.
 */
export function installFirestoreRecovery(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', event => {
    const msg = messageFrom(event.error) || event.message || '';
    if (ASSERTION_RE.test(msg)) handleAssertion(msg);
  });

  window.addEventListener('unhandledrejection', event => {
    const msg = messageFrom((event as PromiseRejectionEvent).reason);
    if (ASSERTION_RE.test(msg)) handleAssertion(msg);
  });
}
